import { Upload, FolderZip, Delete } from "@mui/icons-material";
import React, { useRef, useState } from "react";
import styles from "./input.module.scss";
import cx from "classnames";
import Button from "components/Button";
import { useEffect } from "react";
import { COLORS } from "helpers/utils/constants";
import SecureImage from "components/UI/atoms/SecureImage";
import { Box, IconButton } from "@mui/material";
import Input from "components/Input";
import { useTranslation } from "react-i18next";
import Spin from "components/Spin";

export const MAX_FILE_SIZE_IN_MBS = 1.5;

const DropBox = ({
  height,
  width,
  classes,
  rounded,
  children,
  showButton,
  dimensions,
  error,
  file,
  setFile,
  custom,
  fileType,
  video_code,
  iframeCode,
  setIframeCode,
  prevValue = null,
  disabled,
  hideDelete,
  contentType,
  loading = false,
}) => {
  const inputRef = useRef();
  const [image, setImage] = useState(file);
  const [code, setCode] = useState(iframeCode);
  const [errorMessage, setError] = useState(null);
  const { t } = useTranslation("common");
  const handleFileUpload = () => {
    if (!disabled) {
      inputRef.current.click();
    }
  };

  useEffect(() => {
    if (!!file) setImage(file);
    if (!file) setImage(null);
  }, [file]);

  useEffect(() => {
    setCode(iframeCode);
  }, [iframeCode]);

  const validateImageDimensions = (file, dimensions) => {
    return new Promise((resolve) => {
      const img = new Image();
      img.src = URL.createObjectURL(file);
      img.onload = () => {
        const width = img.width;
        const height = img.height;

        const [minWidth, minHeight] = dimensions;
        const isWidthValid = minWidth ? width >= minWidth : true;

        const isHeightValid = minHeight ? height >= minHeight : true;

        resolve(isWidthValid && isHeightValid);
      };
    });
  };

  const handleFileChange = async (e) => {
    setError(null);
    const _file = e.target.files[0];

    if (_file) {
      if (
        fileType &&
        !_file?.type?.toLowerCase()?.includes(fileType?.toLowerCase())
      ) {
        setError(t("Only %{fileType} files are allowed!", { fileType }));
        return;
      }

      const maxFileSize = MAX_FILE_SIZE_IN_MBS * 1024 * 1024;

      if (_file.size > maxFileSize && contentType !== "scorm") {
        const fileSize = maxFileSize / (1024 * 1024);
        setError(
          t("File size should be less than ${fileSize} MB!", { fileSize })
        );
        return;
      }

      if (dimensions && !(await validateImageDimensions(_file, dimensions))) {
        setError(t(`Image does not meet specified dimensions!`));
        return;
      }

      !!setFile && setFile(_file);
    }
  };

  function isValidVideoCode(htmlString) {
    if (!htmlString) return false;

    const parser = new DOMParser();
    const doc = parser.parseFromString(htmlString, "text/html");
    const iframe = doc.querySelector("iframe");

    if (!iframe) return false;
    if (iframe.tagName !== "IFRAME") return false;
    function isValidUrl(url) {
      try {
        const parsed = new URL(url);
        return !!parsed.hostname && !!parsed.protocol;
      } catch (_) {
        return false;
      }
    }
    if (iframe.hasAttribute("src") && !isValidUrl(iframe.getAttribute("src")))
      return false;

    return true;
  }

  const handleCodeChange = (e) => {
    const _code = e.target.value;
    setCode(_code);
    if (isValidVideoCode(_code)) {
      setIframeCode(_code);
    }
  };

  const handleFileRemove = () => {
    !!setFile && setFile(null);
  };

  const checkIfItIsIframe = (code) => {
    return typeof code === "string" && code && code.includes("<iframe");
  };

  const handleDragOver = (e) => {
    e.preventDefault(); // Prevent the default behavior (prevent file open)
  };

  const handleDrop = async (e) => {
    e.preventDefault();
    const droppedFile = e.dataTransfer.files[0];
    if (droppedFile) {
      await handleFileChange({ target: { files: [droppedFile] } });
    }
  };

  const hasError = Boolean(errorMessage || error);

  return (
    <>
      <div
        data-testid="drop-box"
        style={{
          height: !!height ? height : "200px",
          width: !!width ? width : "200px",
          borderRadius: rounded && "100%",
          borderColor: hasError && COLORS.ERROR,
          position: "relative",
          pointerEvents: disabled ? "none" : "auto", // Disable interactions when disabled
          opacity: disabled ? 0.5 : 1, // Update opacity when disabled
          cursor: disabled ? "not-allowed" : "auto",
        }}
        className={cx(styles.dropbox__input, classes)}
        onDragOver={handleDragOver}
        onDrop={handleDrop}
      >
        {loading && (
          <div className="absolute z-50 flex-center w-full h-full bg-white bg-opacity-50">
            <div className="flex-center  flex-column w-full">
              <Spin blue big />
              <p className="mt-2">
                {t(
                  `Please wait while we are uploading your {{file}}.`,
                  contentType === "scorm"
                    ? { file: "SCORM package" }
                    : { file: "file" }
                )}
              </p>
            </div>
          </div>
        )}
        {!!image && !checkIfItIsIframe(image) ? (
          image?.type?.includes("csv") ? (
            <Box
              className="mx-auto flex flex-column items-center"
              onClick={handleFileUpload}
            >
              <SecureImage
                height={100}
                src="data:image/png;base64,iVBORw0KGgoAAAANSUhEUgAAANIAAADwCAMAAABCI8pNAAAAkFBMVEVFsFj///+i2KtLsl1RtGJBr1VxwH48rVGj2Ks0nEKm268wmj5esGkzq0o3rE0vqkea1aT0+vX2+/d8xIeo1q/R6dWf0qfL5s+HyJGTzZyZ1aPs9u7X7NrC4sel1axduGx/xYppvXeLypXh8eS63sCx3rg9pk1Yt2it3bXB5MZzvH3f8eJYsGRDo1BBok4mljaHx6A1AAAJnUlEQVR4nO2da5viKBOGY5Rmu3sAT+1hPcZ22p3X2Z3//+9eE4ok5qAUcVdqLp5PM2mM3CkoqgBD1CtrtVm+nKInSXz2HqKo9O/jTknOnkUU8T8fw1QgbZh8Hk6GFA9Xj0QaJeK5QClSPBw9Dun8ZAtppD/iePwopA/1fCKN9MeDkM7q2TipMqT47SFIYw9aXWSQ4r8egZTwZ9NkAqT4e3ekjRfNrkCKj52RIi+aXQlp+KMj0kY8mwWUI8XDTTekxBMjlZDi4bwL0siTnnSFFA8/OiBt5LNRjMpI8fDsjjTzw4NHFaTYPSyPJr50pQqSe1ge7XxFcmaKBs8myVVFimO3VCPqP5skVx3JLdXwG8kpLPcbyYnJcySXVMN3pCE+1fAdySHV8B8JnWr4j4QOywkgYcNyCkjxcP3bIcVDTFhOAwnFRAQJE5ZTQUKsAJBBsg/LCSHZMlFCsgzLKSFZhuWkkOxWAGghDW3CclpIVmE5MSSbFQBqSBapBjmk+6kGPaS7KwAEke6tAFBEuhOWk0S6zUQT6WaqQRTpVlhOFql9sxFZpPawnC5SKxNhpLZUgzJSywoAaaTmVMMnpCEWqTnV8AlpiSVqDss9QmILtJUaVwB8QvrfGx4p/rOWaniEFP10QaqvAPiE9OWEVAvLvUJy6UxxLSz3CYl9czNTPBz7ihS9owdbo7GvSNzJ56V68xWpg5n+8hXpy7U3lcNyv5Ci94MrU7HZyDOk6NXVSsWqhm9I7O831+5kUg3fkKKvv527E6wAeIcUff0zdO5PH34iRV/vC2emtZ9IUfTr59CxR6UrAF4iRa+/fh7enKguYbmfSJfG9+v122L4hlc88hQphXp///X+z89vaHmLdIF6fX19d5DHSBoKL6+RLvpCY/ltJRexl4DkvwISBQUkCgpIFBSQKCggUVBAoqCAREEBiYICEgUFJAoKSBQUkCgoIFFQQKKggERBAYmCAhIFBSQKCkgUFJAoKCBRUECioIBEQQGJggISBQUkCgpIFBSQKCggUVBAoqCAREEBiYICEgUFJAoKSBQUkK4/yzPdPykxK8j+qxMVXZG4VDxZLGez5X4nxI2jo5lULNkvl4fJTipZP85TMH7n2FKuIoE5BtQJiUmx/1F6J93ndNdyZjlXk01RcLU58Mr582zd690+ipVPxr3eAXFaqwMSE4P661I/Jw1QTM1q75OeJ+WCIntR5PcbFWb97GOIY4LxSDxqPsLgPKhWTO4aX/q9PhUFmb5046RmOc1KHO3NhEZSy0agVMvr44nFoa3gNi/I9YVDe19R2s63DNkNiYnSiQzj83yzmZcscSwzyYJotZ7P12WLrY1ZhH7r6rr1rGY20Z9AnECLQ2I8f/nm5+ykhJRSKLnI3wZbOpCd7UzBA08LCqWSaY5lTjI2NR601Rge4SfifGoUEuOmTudEFeMRF4N5paqXBrMyrTG/dPHnCbw3Nndyany7XbG7LbMbkjI2WlTcG1Mv0MRMi+J7feFFVgtmDEtTRej94xYr8Jm+DeakdwySAle36tefGY8yq3zmnUSbY1urC5cXixaH0YOP7rUcQA22Rvg7FBKHdr9qjBUYT13TwsCK9qcr+v3SZXAQH412YIm+zQkTTCGQhG72o5agjvWP84l5muAcWip69R94UI33lLpdnFGH19sjQatvd6dMFuaDito0GBh4tk0OQOKdAwpJ335q1awB6Xb0BvfVj2rVYAm+xDsHBBLcvs01VW+btNazVjQC31i3PnhYlHNAIMHtZ5ZtAMaTpcUDBgexqRU1ozXKOdgjGWdr+8DMSFsdwZpuPWm5tTw6OAd7JGh3c9tmbcbI3rx/N38DB7GsloOBYIFyDvZI4E7tfY/MXx39sRfiZjYPDqIax/EFpvcWskUSuiu1hpc1mYFZm2rZV+3JvHEQleFB6QAEkVbA7SyRlP5WxBOT14nV6jgRbU0QHMS1ZzO91/4pmg/aNjzdNjAjhJj0Kvo4yOZgCkpe3R2aY3sq1SZLJHhka9Sg15TSbxonXsBBXPVUuIZ1DvZIJ/2YkeO4ONWhPk71zgEWKbtrsBzaOfzLSFncd6idgLKtVdM4iFK/gf6Fdg7/bsPT4kJMjtdTRR81pjoAc3QOWPdgE7M1fYlU/eX8FlOtmcFQjXcOCCeuR043pPR7LsbaF01wVm1P4Az2xhlAQLVHOwfEUKvPmkHMPdXFRT+fpq3eBxyE6awQyTs4B0TD05WxDcTbvk0kECdV0y7jICDlhfjLLjmrfolt2LqouVkn8Ugz1SbmYMJuqyEg3+y7NAr7FNDZA1XuA7Nh1ecPDmKUoULc7+JgEUiNcZiLwBHUeiVcz5JbyDf3Ts/PGimfyelqJjkvqn51fZtdTzMySGednANmOgXcan2YREr+aEYyDoLnvsjJOWCQwEH0lq3T10KV/sTasj5owfUJBfjDxamKDs4BNTVpZsSTZkfOk/Nqmvdn3l/smtlh0K73fPYCvpAfrsYorBBIELpemJrqynflcSvredOmNgoe79xQX+M4IJ1tmSa/X0/ENH+epy7qdRX6EZuVC22KNa8ZlMGcRNOYDQ7iyEru3EG4xRgTz8z5dXbKFcwu52kvFCytLumCElpvswW0o5t2cQ7YVUCVh9PTKJ/1ucTZC5M8mBxUmfmh1VLIUsEJrLA317e8auo+WiDXalWxPWA9S4S6iE+O+U6AfHKVleYd5stTWk7JZGvIW5IUcBCdnAN+Rf16QX28WpVPelsU1eC7UXvBcZsFVJEqujoHh00C8tR20vzqalKByebtEWnB1jbFt6aMs3Nw2crBip5zpW116kfs6meUpjremCbnxpa4JaXrCjrsIeJqX50iGU0bNjcxtatb6ljbxHL1kZ1mmjr3JNedXlzwxdE0wPH5+4tqrufFx71M16O84HF/a1NY9gm+XZ83SQci9/14XArFB7vdIN2ocaOaLN3s0d/dL5jf+OZeOAt12zXJmOXGQeuCD1DYCEpBAYmCAhIFBSQKCkgUFJAoKCBRUECioN8TafDsOjxYF6ROC/8eiu0j93lNP8WXUce9DN5JHiOb/emUJM5Rh+lnLyV7EebXkATEDxek+j56ylLnC5LraryXSveIXZDmv1FvSrcypGu+L7+NH89W/VOkscXrAEhI783IVuYxv1v1WPoHiRqpt77/oxz/xaRecoVdF5/1nSTUJAew3mg2koxfaBuKqfxH8QYp+/UUWSimTsX6fYHU620SJf+7l5w8SIxxqSblNf4yUq83ms8muwEpJfvt/Iqh9380XZ/KGGaPGQAAAABJRU5ErkJggg=="
                alt="csv"
              />
              <p className="c-success text-center w-full">{image?.name}</p>
            </Box>
          ) : (
            <>
              {fileType === "zip" ? (
                <FolderZip
                  style={{
                    fontSize: "150px",
                  }}
                />
              ) : (
                <SecureImage
                  src={
                    typeof image === "string"
                      ? image
                      : URL.createObjectURL(image)
                  }
                  alt="file"
                  height="100%"
                  width="100%"
                  containerClasses="w-full"
                  classes={`object-cover cursor-pointer ${
                    rounded && "rounded-100"
                  }`}
                  onClick={handleFileUpload}
                />
              )}
            </>
          )
        ) : (
          <div>
            <Upload style={{ fontSize: 110 }} />
            {custom ? (
              children
            ) : (
              <>
                {showButton ? (
                  <>
                    <p className="fs-12">{t("Drag & drop")}</p>
                    <p className="mx-1 fs-14">{t("OR")}</p>
                    <Button onClick={handleFileUpload} bordered>
                      <Upload /> {t("Browse your files")}
                    </Button>
                  </>
                ) : (
                  <>
                    <div className="w-full">
                      <p className="fs-12 w-full">
                        {t("Drag & drop or")}
                        <span onClick={handleFileUpload} className="mx-1">
                          {t("browse")}
                        </span>
                        {contentType === "scorm"
                          ? t("your SCORM package")
                          : fileType === "zip"
                          ? t("your files")
                          : t("your images")}
                      </p>
                      {video_code && (
                        <div>
                          <div className="w-full py-2">{t("Or")}</div>
                          <div className="flex justify-center">
                            <Input
                              onChange={handleCodeChange}
                              placeholder={t("Insert embedded video code")}
                              value={code}
                            />
                          </div>
                          <p className="fs-12 text-center w-full c-error pb-2">
                            {/* Display error message based on validation */}
                            {code &&
                              code.trim().length > 0 &&
                              !isValidVideoCode(code) &&
                              t("Please enter a valid embedded video code.")}
                          </p>
                        </div>
                      )}
                    </div>
                  </>
                )}
                {children}
              </>
            )}
          </div>
        )}
        <div>
          {!!file === false && prevValue && fileType === "zip" && (
            <div className="flex items-center py-2">
              <FolderZip
                style={{
                  fontSize: "40px",
                }}
              />
              <span>{prevValue["title"]}</span>
            </div>
          )}
        </div>
        <input hidden type="file" ref={inputRef} onChange={handleFileChange} />
        {!hideDelete && !!file && !checkIfItIsIframe(file) && (
          <div
            className="absolute flex-center justify-center"
            style={{ top: "40%" }}
          >
            <IconButton
              className="rounded-100 border-error"
              onClick={handleFileRemove}
            >
              <Delete color="error" fontSize="small" />
            </IconButton>
          </div>
        )}
      </div>
      {hasError && <p className={styles.error}>{t(error || errorMessage)}</p>}
    </>
  );
};

export default DropBox;
